import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import {
  Megaphone,
  Target,
  DollarSign,
  TrendingUp,
  RefreshCw,
  Link2,
  Link2Off,
  Users,
  BarChart3,
  MousePointerClick,
  Percent,
  ArrowUpRight,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Eye,
  Pause,
  Play,
  Calendar
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Meta Ads campaign status badges
const statusConfig = {
  active: { 
    label: 'Activa', 
    class: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    icon: Play
  },
  paused: { 
    label: 'Pausada', 
    class: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    icon: Pause
  },
  archived: { 
    label: 'Archivada', 
    class: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    icon: Eye
  }
};

// Objective labels
const objectiveLabels = {
  LEAD_GENERATION: 'Generación de Leads',
  CONVERSIONS: 'Conversiones',
  BRAND_AWARENESS: 'Reconocimiento de Marca',
  TRAFFIC: 'Tráfico',
  ENGAGEMENT: 'Interacción',
  VIDEO_VIEWS: 'Reproducciones de Video'
};

export const CampaignsPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const location = useLocation();
  const didHandleCallback = useRef(false);

  // Connection state
  const [connectionStatus, setConnectionStatus] = useState({
    connected: false,
    status: 'disconnected',
    ad_account_id: null,
    last_sync_at: null,
    connected_at: null
  });
  const [connectionModalOpen, setConnectionModalOpen] = useState(false);
  
  // Campaigns state
  const [campaigns, setCampaigns] = useState([]);
  const [kpis, setKpis] = useState({
    active_campaigns: 0,
    total_spend: 0,
    total_leads: 0,
    leads_crm: 0,
    cpl: 0,
    avg_ctr: 0,
    conversions: 0,
    revenue: 0,
    roi: 0
  });
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Admin filter
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('all');

  // Fetch connection status
  const fetchConnectionStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/campaigns/meta/status`);
      setConnectionStatus(response.data);
    } catch (error) {
      console.error('Error fetching connection status:', error);
    }
  }, []);

  // Fetch campaigns
  const fetchCampaigns = useCallback(async () => {
    try {
      const params = {};
      if (isAdmin && selectedAgent !== 'all') {
        params.agent_id = selectedAgent;
      }
      const response = await axios.get(`${API}/campaigns/meta`, { params });
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setCampaigns([]);
    }
  }, [isAdmin, selectedAgent]);

  // Fetch KPIs
  const fetchKpis = useCallback(async () => {
    try {
      const params = {};
      if (isAdmin && selectedAgent !== 'all') {
        params.agent_id = selectedAgent;
      }
      const response = await axios.get(`${API}/campaigns/meta/kpis`, { params });
      setKpis(response.data);
    } catch (error) {
      console.error('Error fetching KPIs:', error);
    }
  }, [isAdmin, selectedAgent]);

  // Fetch agents for admin filter
  const fetchAgents = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const response = await axios.get(`${API}/campaigns/meta/agents`);
      setAgents(response.data);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  }, [isAdmin]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchConnectionStatus(),
        fetchCampaigns(),
        fetchKpis(),
        fetchAgents()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchConnectionStatus, fetchCampaigns, fetchKpis, fetchAgents]);

  // Reload when agent filter changes
  useEffect(() => {
    if (!loading) {
      fetchCampaigns();
      fetchKpis();
    }
  }, [selectedAgent, fetchCampaigns, fetchKpis, loading]);

  // Detect OAuth callback redirect (?meta_connected=1)
  useEffect(() => {
    if (didHandleCallback.current) return;
    const params = new URLSearchParams(location.search);
    if (params.get('meta_connected') === '1') {
      didHandleCallback.current = true;
      toast.success('Meta Ads conectado correctamente');
      fetchConnectionStatus();
      fetchCampaigns();
      fetchKpis();
      window.history.replaceState({}, '', window.location.pathname);
    }
    const metaError = params.get('meta_error');
    if (metaError) {
      didHandleCallback.current = true;
      toast.error(`Error al conectar con Meta: ${metaError}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [location.search, fetchConnectionStatus, fetchCampaigns, fetchKpis]);

  // Connect to Meta Ads via OAuth
  const handleConnect = async () => {
    setConnecting(true);
    try {
      const response = await axios.get(`${API}/integrations/meta/connect`);
      const authUrl = response.data.auth_url;
      toast.info('Completa la autorización en la ventana de Meta');
      window.open(authUrl, '_blank', 'width=650,height=700,left=200,top=100');
      setConnectionModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar conexión con Meta');
    } finally {
      setConnecting(false);
    }
  };

  // Disconnect from Meta Ads
  const handleDisconnect = async () => {
    try {
      await axios.post(`${API}/campaigns/meta/disconnect`);
      toast.success('Desconectado de Meta Ads');
      setConnectionStatus({
        connected: false,
        status: 'disconnected',
        ad_account_id: null,
        last_sync_at: null,
        connected_at: null
      });
      setConnectionModalOpen(false);
      setCampaigns([]);
      setKpis({
        active_campaigns: 0,
        total_spend: 0,
        total_leads: 0,
        leads_crm: 0,
        cpl: 0,
        avg_ctr: 0,
        conversions: 0,
        revenue: 0,
        roi: 0
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al desconectar');
    }
  };

  // Sync campaigns
  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/campaigns/meta/sync`);
      toast.success(response.data.message || 'Sincronización completada');
      setConnectionStatus(prev => ({
        ...prev,
        last_sync_at: response.data.synced_at
      }));
      await Promise.all([fetchCampaigns(), fetchKpis()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al sincronizar');
    } finally {
      setSyncing(false);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // Connection status icon
  const ConnectionStatusIcon = () => {
    if (connectionStatus.status === 'connected') {
      return <CheckCircle2 className="h-5 w-5 text-green-500" />;
    } else if (connectionStatus.status === 'expired') {
      return <AlertCircle className="h-5 w-5 text-yellow-500" />;
    }
    return <XCircle className="h-5 w-5 text-gray-400" />;
  };

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Campañas de Marketing</h1>
          <p className="text-muted-foreground">Métricas de Meta Ads conectadas con tu CRM</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Admin filter */}
          {isAdmin && agents.length > 0 && (
            <Select value={selectedAgent} onValueChange={setSelectedAgent}>
              <SelectTrigger className="w-[200px]" data-testid="agent-filter">
                <SelectValue placeholder="Filtrar por agente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los agentes</SelectItem>
                {agents.map((agent) => (
                  <SelectItem key={agent.id} value={agent.id}>
                    {agent.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          {/* Sync button (only if connected) */}
          {connectionStatus.connected && (
            <Button
              variant="outline"
              onClick={handleSync}
              disabled={syncing}
              data-testid="sync-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Sincronizando...' : 'Sincronizar'}
            </Button>
          )}
          
          {/* Connect button */}
          <Button
            className={connectionStatus.connected 
              ? 'bg-green-600 hover:bg-green-700' 
              : 'bg-[#1877F2] hover:bg-[#166FE5]'
            }
            onClick={() => setConnectionModalOpen(true)}
            data-testid="connect-meta-btn"
          >
            {connectionStatus.connected ? (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                <div className="flex flex-col items-start leading-tight">
                  <span className="text-sm font-medium">Meta Ads Conectado</span>
                  <span className="text-[10px] opacity-80">✓ Cuenta sincronizada</span>
                </div>
              </div>
            ) : (
              <>
                <Link2 className="h-4 w-4 mr-2" />
                Conectar Meta Ads
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Connection Modal */}
      <Dialog open={connectionModalOpen} onOpenChange={setConnectionModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ConnectionStatusIcon />
              {connectionStatus.connected ? 'Meta Ads Conectado' : 'Conectar Meta Ads'}
            </DialogTitle>
            <DialogDescription>
              {connectionStatus.connected 
                ? 'Gestiona tu conexión con Meta Ads'
                : 'Conecta tu cuenta de Meta Ads para ver métricas de campañas'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {connectionStatus.connected ? (
              <>
                {/* Connected State */}
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-400 font-medium mb-2">
                    <CheckCircle2 className="h-5 w-5" />
                    Conexión Activa
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Ad Account ID:</span>
                      <span className="font-mono">{connectionStatus.ad_account_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Conectado:</span>
                      <span>{formatDate(connectionStatus.connected_at)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Última sincronización:</span>
                      <span>{formatDate(connectionStatus.last_sync_at)}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={handleSync}
                    disabled={syncing}
                    data-testid="modal-sync-btn"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                    Sincronizar ahora
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1"
                    onClick={handleDisconnect}
                    data-testid="disconnect-btn"
                  >
                    <Link2Off className="h-4 w-4 mr-2" />
                    Desconectar
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Disconnected State */}
                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <h4 className="font-medium mb-2">¿Qué puedes hacer con Meta Ads?</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li className="flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-blue-500" />
                      Ver métricas de campañas en tiempo real
                    </li>
                    <li className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-green-500" />
                      Relacionar leads con campañas específicas
                    </li>
                    <li className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-yellow-500" />
                      Calcular ROI real de tus campañas
                    </li>
                    <li className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-purple-500" />
                      Medir conversiones de lead a cliente
                    </li>
                  </ul>
                </div>
                
                {connectionStatus.status === 'expired' && (
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                    <div className="flex items-center gap-2 text-yellow-700 dark:text-yellow-400">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-sm">Tu token ha expirado. Vuelve a conectar.</span>
                    </div>
                  </div>
                )}
                
                <Button
                  className="w-full bg-[#1877F2] hover:bg-[#166FE5]"
                  onClick={handleConnect}
                  disabled={connecting}
                  data-testid="modal-connect-btn"
                >
                  {connecting ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Conectando...
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                      Conectar con Meta
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Campañas Activas</p>
                <p className="text-2xl font-bold">{kpis.active_campaigns}</p>
              </div>
              <div className="p-2 rounded-lg bg-[#2d8659] text-white">
                <Megaphone className="h-4 w-4" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Inversión Total</p>
                <p className="text-2xl font-bold">{formatCurrency(kpis.total_spend)}</p>
              </div>
              <div className="p-2 rounded-lg bg-[#1a365d] text-white">
                <DollarSign className="h-4 w-4" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Leads Generados</p>
                <p className="text-2xl font-bold">{kpis.total_leads}</p>
                {kpis.leads_crm > 0 && (
                  <p className="text-xs text-muted-foreground">{kpis.leads_crm} en CRM</p>
                )}
              </div>
              <div className="p-2 rounded-lg bg-[#1877F2] text-white">
                <Users className="h-4 w-4" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Costo por Lead</p>
                <p className="text-2xl font-bold">{formatCurrency(kpis.cpl)}</p>
              </div>
              <div className="p-2 rounded-lg bg-purple-600 text-white">
                <Target className="h-4 w-4" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">CTR Promedio</p>
                <p className="text-2xl font-bold">{kpis.avg_ctr.toFixed(2)}%</p>
              </div>
              <div className="p-2 rounded-lg bg-orange-500 text-white">
                <MousePointerClick className="h-4 w-4" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Conversiones</p>
                <p className="text-2xl font-bold">{kpis.conversions}</p>
              </div>
              <div className="p-2 rounded-lg bg-green-600 text-white">
                <ArrowUpRight className="h-4 w-4" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={kpis.roi >= 0 ? 'border-green-200 dark:border-green-800' : 'border-red-200 dark:border-red-800'}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">ROI Estimado</p>
                <p className={`text-2xl font-bold ${kpis.roi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {kpis.roi.toFixed(1)}%
                </p>
              </div>
              <div className={`p-2 rounded-lg ${kpis.roi >= 0 ? 'bg-green-600' : 'bg-red-600'} text-white`}>
                <Percent className="h-4 w-4" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Campaigns Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full text-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1877F2] mx-auto"></div>
            <p className="text-muted-foreground mt-2">Cargando campañas...</p>
          </div>
        ) : !connectionStatus.connected ? (
          <Card className="col-span-full">
            <CardContent className="p-8 text-center">
              <div className="mx-auto w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                <Link2Off className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium mb-2">Meta Ads no conectado</h3>
              <p className="text-muted-foreground mb-4">
                Conecta tu cuenta de Meta Ads para ver tus campañas y métricas
              </p>
              <Button 
                className="bg-[#1877F2] hover:bg-[#166FE5]"
                onClick={() => setConnectionModalOpen(true)}
              >
                <Link2 className="h-4 w-4 mr-2" />
                Conectar Meta Ads
              </Button>
            </CardContent>
          </Card>
        ) : campaigns.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="p-8 text-center">
              <Megaphone className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No hay campañas</h3>
              <p className="text-muted-foreground mb-4">
                No se encontraron campañas en tu cuenta de Meta Ads
              </p>
              <Button variant="outline" onClick={handleSync} disabled={syncing}>
                <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                Sincronizar
              </Button>
            </CardContent>
          </Card>
        ) : (
          campaigns.map((campaign) => {
            const status = statusConfig[campaign.status] || statusConfig.active;
            const StatusIcon = status.icon;
            
            return (
              <Card 
                key={campaign.id} 
                className="hover:shadow-md transition-shadow"
                data-testid={`campaign-${campaign.meta_campaign_id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-base truncate" title={campaign.name}>
                        {campaign.name}
                      </CardTitle>
                      <CardDescription className="text-xs">
                        {objectiveLabels[campaign.objective] || campaign.objective}
                      </CardDescription>
                    </div>
                    <Badge className={`${status.class} border-0 ml-2 shrink-0`}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {status.label}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Meta ID */}
                  <p className="text-xs text-muted-foreground mb-3 font-mono">
                    ID: {campaign.meta_campaign_id}
                  </p>
                  
                  {/* Main metrics */}
                  <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                    <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded">
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <DollarSign className="h-3 w-3" /> Inversión
                      </p>
                      <p className="font-semibold">{formatCurrency(campaign.spend)}</p>
                    </div>
                    <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded">
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <MousePointerClick className="h-3 w-3" /> CTR
                      </p>
                      <p className="font-semibold">{campaign.ctr?.toFixed(2) || 0}%</p>
                    </div>
                    <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded">
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Target className="h-3 w-3" /> CPL
                      </p>
                      <p className="font-semibold">
                        {campaign.leads_count > 0 
                          ? formatCurrency(campaign.spend / campaign.leads_count)
                          : '-'
                        }
                      </p>
                    </div>
                    <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded">
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Users className="h-3 w-3" /> Leads
                      </p>
                      <p className="font-semibold">
                        {campaign.leads_count}
                        {campaign.leads_crm > 0 && (
                          <span className="text-xs text-green-600 ml-1">
                            ({campaign.leads_crm} CRM)
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  
                  {/* CRM metrics */}
                  <div className="border-t pt-3 mt-3">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Métricas CRM</p>
                    <div className="flex justify-between text-sm">
                      <div>
                        <span className="text-muted-foreground">Conversiones:</span>
                        <span className="font-medium ml-1">{campaign.conversions || 0}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Ingresos:</span>
                        <span className="font-medium ml-1 text-green-600">
                          {formatCurrency(campaign.revenue || 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Dates */}
                  {(campaign.start_date || campaign.end_date) && (
                    <div className="border-t pt-3 mt-3 flex items-center gap-1 text-xs text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      <span>
                        {campaign.start_date || '?'} - {campaign.end_date || 'En curso'}
                      </span>
                    </div>
                  )}
                  
                  {/* Last sync */}
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-2">
                    <Clock className="h-3 w-3" />
                    <span>Sincronizado: {formatDate(campaign.synced_at)}</span>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
      
    </div>
  );
};
